import { useState, useEffect, useMemo } from "react";
import { useUpdateBusinessdetailMutation, useDeleteBusinessdetailMutation } from "./businessdetailsApiSlice";
import { useParams, useNavigate } from "react-router-dom";

// small helper: ensure value fits <input type="date">
const toDateInput = (d) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
};

export default function EditBusinessdetailForm({ businessdetail }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const targetId = businessdetail?._id || routeId;

  const [updateBusinessdetail, { isLoading, isSuccess, isError, error }] = useUpdateBusinessdetailMutation();
  const [deleteBusinessdetail, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
    useDeleteBusinessdetailMutation();

  // --- form state (prefill from businessdetail) ---
  const [title, setTitle] = useState(businessdetail?.title || "");
  const [shortTitle, setShortTitle] = useState(businessdetail?.shortTitle || "");
  const [description, setDescription] = useState(businessdetail?.description || "");
  const [status, setStatus] = useState(businessdetail?.status || "open");

  // Extended fields
  const [entrepreneur, setEntrepreneur] = useState(businessdetail?.entrepreneur || "");
  const [entrepreneurName, setEntrepreneurName] = useState(businessdetail?.entrepreneurName || "");
  const [organizationName, setOrganizationName] = useState(businessdetail?.organizationName || "");
  const [projectImpactCategory, setProjectImpactCategory] = useState(businessdetail?.projectImpactCategory || "");
  const [submissionDate, setSubmissionDate] = useState(toDateInput(businessdetail?.submissionDate));

  // Finance
  const [funding_request, setFundingRequest] = useState(businessdetail?.finance?.funding_request ?? 0);
  const [funding_request_opex, setFundingRequestOpex] = useState(businessdetail?.finance?.funding_request_opex ?? 0);
  const [funding_request_capex, setFundingRequestCapex] = useState(businessdetail?.finance?.funding_request_capex ?? 0);

  // Founders
  const [founders, setFounders] = useState(
    businessdetail?.founder?.length
      ? businessdetail.founder.map(f => ({ name: f.name || "", expertise: f.expertise || "" }))
      : [{ name: "", expertise: "" }]
  );

  // Business Model Canvas 9 blocks
  const [keyPartners, setKeyPartners] = useState(businessdetail?.components?.keyPartners || [""]);
  const [keyActivities, setKeyActivities] = useState(businessdetail?.components?.keyActivities || [""]);
  const [keyResources, setKeyResources] = useState(businessdetail?.components?.keyResources || [""]);
  const [valuePropositions, setValuePropositions] = useState(businessdetail?.components?.valuePropositions || [""]);
  const [customerRelationships, setCustomerRelationships] = useState(businessdetail?.components?.customerRelationships || [""]);
  const [channels, setChannels] = useState(businessdetail?.components?.channels || [""]);
  const [customerSegments, setCustomerSegments] = useState(businessdetail?.components?.customerSegments || [""]);
  const [costStructure, setCostStructure] = useState(businessdetail?.components?.costStructure || [""]);
  const [revenueStreams, setRevenueStreams] = useState(businessdetail?.components?.revenueStreams || [""]);

  // Validation (basic)
  const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
  const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
  const canSave = validTitle && validShort && !!targetId && !isLoading;

  // Helper for array fields
  const handleArrayChange = (setter, arr, idx, value) => {
    const newArr = [...arr];
    newArr[idx] = value;
    setter(newArr);
  };
  const handleAddArrayItem = (setter, arr) => setter([...arr, ""]);
  const handleRemoveArrayItem = (setter, arr, idx) => setter(arr.filter((_, i) => i !== idx));

  // Founders helpers
  const handleFounderChange = (idx, field, value) => {
    const newArr = [...founders];
    newArr[idx][field] = value;
    setFounders(newArr);
  };
  const handleAddFounder = () => setFounders([...founders, { name: "", expertise: "" }]);
  const handleRemoveFounder = (idx) => setFounders(founders.filter((_, i) => i !== idx));

  // After save/delete → go back
  useEffect(() => {
    if (isSuccess || isDelSuccess) {
      navigate("/dash/businessdetails");
    }
  }, [isSuccess, isDelSuccess, navigate]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    try {
      await updateBusinessdetail({
        id: targetId,
        title,
        shortTitle,
        description,
        status,
        entrepreneur,
        entrepreneurName,
        organizationName,
        projectImpactCategory,
        submissionDate: submissionDate ? new Date(submissionDate) : undefined,
        finance: {
          funding_request: Number(funding_request),
          funding_request_opex: Number(funding_request_opex),
          funding_request_capex: Number(funding_request_capex),
        },
        founder: founders.filter(f => f.name || f.expertise),
        components: {
          keyPartners: keyPartners.filter(Boolean),
          keyActivities: keyActivities.filter(Boolean),
          keyResources: keyResources.filter(Boolean),
          valuePropositions: valuePropositions.filter(Boolean),
          customerRelationships: customerRelationships.filter(Boolean),
          channels: channels.filter(Boolean),
          customerSegments: customerSegments.filter(Boolean),
          costStructure: costStructure.filter(Boolean),
          revenueStreams: revenueStreams.filter(Boolean),
        },
      }).unwrap();
    } catch {
      // handled by error banner
    }
  };

  const onDelete = async () => {
    if (!targetId) return;
    if (!window.confirm(`Delete businessdetail "${title}"?`)) return;
    try {
      await deleteBusinessdetail({ id: targetId }).unwrap();
    } catch {
      // handled by error banner
    }
  };

  const errText = (error?.data?.message || delerror?.data?.message) ?? "";

  return (
    <div className="min-h-full px-4 sm:px-6 lg:px-8 py-6">
      {(isError || isDelError) && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {errText || "Something went wrong."}
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()} className="mt-2">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`mt-1 w-full rounded-xl border ${
                  validTitle ? "border-gray-200" : "border-amber-300"
                } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                placeholder="Main Businessdetail Title"
              />
              {!validTitle && (
                <p className="mt-1 text-xs text-amber-600">Must be 3–80 characters.</p>
              )}
            </div>

            {/* Short title */}
            <div>
              <label htmlFor="shortTitle" className="block text-sm font-medium text-gray-700">
                Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
              </label>
              <input
                id="shortTitle"
                type="text"
                value={shortTitle}
                onChange={(e) => setShortTitle(e.target.value)}
                className={`mt-1 w-full rounded-xl border ${
                  validShort ? "border-gray-200" : "border-amber-300"
                } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                placeholder="Short name"
              />
              {!validShort && (
                <p className="mt-1 text-xs text-amber-600">Must be 3–60 characters.</p>
              )}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description <span className="text-gray-400 font-normal">(optional, multiline)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5"
                placeholder="Enter business description"
                rows={4}
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Entrepreneur */}
            <div>
              <label htmlFor="entrepreneur" className="block text-sm font-medium text-gray-700">
                Entrepreneur
              </label>
              <input
                id="entrepreneur"
                type="text"
                value={entrepreneur}
                onChange={(e) => setEntrepreneur(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Entrepreneur"
              />
            </div>

            {/* Entrepreneur Name */}
            <div>
              <label htmlFor="entrepreneurName" className="block text-sm font-medium text-gray-700">
                Entrepreneur Name
              </label>
              <input
                id="entrepreneurName"
                type="text"
                value={entrepreneurName}
                onChange={(e) => setEntrepreneurName(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Entrepreneur Name"
              />
            </div>

            {/* Organization Name */}
            <div>
              <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">
                Organization Name
              </label>
              <input
                id="organizationName"
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Organization Name"
              />
            </div>

            {/* Project Impact Category */}
            <div>
              <label htmlFor="projectImpactCategory" className="block text-sm font-medium text-gray-700">
                Project Impact Category
              </label>
              <input
                id="projectImpactCategory"
                type="text"
                value={projectImpactCategory}
                onChange={(e) => setProjectImpactCategory(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Project Impact Category"
              />
            </div>

            {/* Submission Date */}
            <div>
              <label htmlFor="submissionDate" className="block text-sm font-medium text-gray-700">
                Submission Date
              </label>
              <input
                id="submissionDate"
                type="date"
                value={submissionDate}
                onChange={(e) => setSubmissionDate(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              />
            </div>

            {/* Finance */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Funding Request</label>
              <input
                type="number"
                value={funding_request}
                onChange={(e) => setFundingRequest(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Total Funding Request"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Funding Request OPEX</label>
              <input
                type="number"
                value={funding_request_opex}
                onChange={(e) => setFundingRequestOpex(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="OPEX Funding"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Funding Request CAPEX</label>
              <input
                type="number"
                value={funding_request_capex}
                onChange={(e) => setFundingRequestCapex(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="CAPEX Funding"
              />
            </div>
          </div>

          {/* Founders */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Founders</label>
            {founders.map((founder, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={founder.name}
                  onChange={e => handleFounderChange(idx, "name", e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                  placeholder="Name"
                />
                <input
                  type="text"
                  value={founder.expertise}
                  onChange={e => handleFounderChange(idx, "expertise", e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                  placeholder="Expertise"
                />
                <button type="button" onClick={() => handleRemoveFounder(idx)} className="text-red-500 px-2">✕</button>
              </div>
            ))}
            <button type="button" onClick={handleAddFounder} className="text-blue-500 px-2">+ Add Founder</button>
          </div>

          {/* Business Model Canvas 9 blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              ["Key Partners", keyPartners, setKeyPartners],
              ["Key Activities", keyActivities, setKeyActivities],
              ["Key Resources", keyResources, setKeyResources],
              ["Value Propositions", valuePropositions, setValuePropositions],
              ["Customer Relationships", customerRelationships, setCustomerRelationships],
              ["Channels", channels, setChannels],
              ["Customer Segments", customerSegments, setCustomerSegments],
              ["Cost Structure", costStructure, setCostStructure],
              ["Revenue Streams", revenueStreams, setRevenueStreams],
            ].map(([label, arr, setter], i) => (
              <div key={label}>
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                {arr.map((val, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={val}
                      onChange={e => handleArrayChange(setter, arr, idx, e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                      placeholder={label}
                    />
                    <button type="button" onClick={() => handleRemoveArrayItem(setter, arr, idx)} className="text-red-500 px-2">✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => handleAddArrayItem(setter, arr)} className="text-blue-500 px-2">+ Add</button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onDelete}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Delete
            </button>
            <button
              type="submit"
              onClick={onSave}
              disabled={!canSave}
              className={`rounded-xl px-4 py-2 text-sm text-white ${
                canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
